import { apiSlice } from './apiSlice'
import { roundsApi } from './roundsApi'

export interface Case {
  id: string
  title: string
  parties_requests?: string  // Yêu cầu các bên
  description?: string  // Mô tả chi tiết (quản lý qua upload tài liệu)
  // Thông tin cơ bản cuộc thi
  case_number?: string
  case_type?: string
  court?: string
  // Các bên liên quan
  plaintiff?: string
  defendant?: string
  // Thông tin thời gian
  incident_date?: string
  filing_date?: string
  // Thông tin khác
  dispute_value?: string
  legal_area?: string
  summary?: string
  // UAT-TC #7
  materials_list?: string
  submission_requirements?: string
  // Trạng thái và phân bổ
  status: string
  current_round_id?: string  // Round ID hiện tại đang diễn ra
  assigned_team_ids?: string[]  // List of team IDs (có thể phân bổ cho nhiều đội)
  assigned_by?: string
  assigned_at?: string
  deadline?: string
  created_at?: string
  updated_at?: string
  // ── R1 (Vòng sơ loại) flow versioning ──────────────────────────────────
  /** 'legacy_single_role' (đội chọn 1 vai) | 'dual_claimant_packets' (2 packet) */
  r1_flow_version?: 'legacy_single_role' | 'dual_claimant_packets' | null
  /** Số đội vào tiếp sau R1 (flow mới — default 15 từ BE) */
  r1_shortlist_size?: number | null
  /** Số đội vào chung kết (default 4) */
  final_advancing_size?: number | null
  /** Tên nguyên đơn cho packet scenario_a (flow `dual_claimant_packets`). Fallback `plaintiff` nếu null. */
  scenario_a_claimant_name?: string | null
  /** Tên nguyên đơn cho packet scenario_b (flow `dual_claimant_packets`). Fallback `defendant` nếu null. */
  scenario_b_claimant_name?: string | null
}

export interface CaseCreate {
  title: string
  parties_requests?: string
  description?: string
  case_number?: string
  case_type?: string
  court?: string
  plaintiff?: string
  defendant?: string
  incident_date?: string
  filing_date?: string
  dispute_value?: string
  legal_area?: string
  summary?: string
  materials_list?: string
  submission_requirements?: string
  deadline?: string
  // P1-#9 (audit batch B): R1 flow versioning fields cho admin set khi tạo/sửa.
  r1_flow_version?: 'legacy_single_role' | 'dual_claimant_packets'
  r1_shortlist_size?: number | null
  final_advancing_size?: number | null
  scenario_a_claimant_name?: string | null
  scenario_b_claimant_name?: string | null
}

export interface CaseExamInfo {
  case_id: string
  title: string
  case_number?: string
  case_type?: string
  summary?: string
  parties_requests?: string
  plaintiff?: string
  defendant?: string
  dispute_value?: string
  legal_area?: string
  materials_list?: string
  submission_requirements?: string
  materials: Array<{
    id: string
    title: string
    file_name: string
    document_type: string
    file_size: number
    mime_type: string
  }>
  /** Tổng số chứng cứ trong pool (label != null). Đội xem chi tiết ở /evidence */
  evidence_pool_count?: number
}

export interface CaseAssign {
  team_ids: string[]  // List of team IDs (có thể chọn nhiều đội)
  deadline?: string
  notes?: string
}

export const casesApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCases: builder.query<Case[], void>({
      query: () => '/cases/list',
      providesTags: ['Case'],
    }),
    getCase: builder.query<Case, string>({
      query: (id) => `/cases/${id}`,
      providesTags: (result, error, id) => [{ type: 'Case', id }],
    }),
    createCase: builder.mutation<Case, CaseCreate>({
      query: (body) => ({
        url: '/cases/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Case'],
    }),
    updateCase: builder.mutation<Case, { id: string } & Partial<CaseCreate>>({
      query: ({ id, ...data }) => ({
        url: `/cases/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Case', id },
        { type: 'Case', id: `exam-${id}` },
        'Case',
      ],
    }),
    assignCase: builder.mutation<Case, { caseId: string; assignment: CaseAssign }>({
      query: ({ caseId, assignment }) => ({
        url: `/cases/${caseId}/assign`,
        method: 'POST',
        body: assignment,
      }),
      invalidatesTags: (result, error, { caseId }) => [{ type: 'Case', id: caseId }, 'Team'],
    }),
    unassignTeamFromCase: builder.mutation<Case, { caseId: string; teamId: string }>({
      query: ({ caseId, teamId }) => ({
        url: `/cases/${caseId}/assign/${teamId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { caseId }) => [
        { type: 'Case', id: caseId },
        'Case',
        'Team',
      ],
    }),
    moveToNextRound: builder.mutation<any, string>({
      query: (caseId) => ({
        url: `/cases/${caseId}/move-to-next-round`,
        method: 'POST',
      }),
      // Optimistic patch — update cả Case.current_round_id và Round[].status để UI thay đổi instant
      async onQueryStarted(caseId, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          const newRoundId = (data && (data.current_round_id || data.current_round?.id)) as string | undefined
          if (!newRoundId) return
          patchAfterMoveRound({ dispatch, caseId, newRoundId, meetingLink: data?.meeting_link })
        } catch {}
      },
      invalidatesTags: (result, error, caseId) => [
        { type: 'Case', id: caseId },
        { type: 'Case', id: `exam-${caseId}` },
        'Case',
        'Round',
      ],
    }),
    moveToRound: builder.mutation<{ message: string; current_round_id: string; meeting_link?: string }, { caseId: string; roundId: string }>({
      query: ({ caseId, roundId }) => ({
        url: `/cases/${caseId}/move-to-round/${roundId}`,
        method: 'POST',
      }),
      async onQueryStarted({ caseId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          const newRoundId = data?.current_round_id
          if (!newRoundId) return
          patchAfterMoveRound({ dispatch, caseId, newRoundId, meetingLink: data?.meeting_link })
        } catch {}
      },
      invalidatesTags: (result, error, { caseId }) => [
        { type: 'Case', id: caseId },
        { type: 'Case', id: `exam-${caseId}` },
        'Case',
        'Round',
      ],
    }),
    deleteCase: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/cases/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Case', 'Round'],
    }),
    getExamInfo: builder.query<CaseExamInfo, string>({
      query: (caseId) => `/cases/${caseId}/exam-info`,
      providesTags: (result, error, id) => [{ type: 'Case', id: `exam-${id}` }],
    }),
    getEvidencePoolSummary: builder.query<
      {
        case_id: string
        correct_count: number
        decoy_count: number
        min_correct: number
        min_decoy: number
        ready_for_assign: boolean
      },
      string
    >({
      query: (caseId) => `/cases/${caseId}/evidence-pool-summary`,
      providesTags: (result, error, id) => [
        { type: 'Case', id: `pool-${id}` },
        { type: 'Document', id: `case-${id}` },
      ],
    }),
  }),
})

/**
 * Patch các cache liên quan ngay sau khi mutation move-to-round thành công,
 * để UI cập nhật instant (không phải chờ tag invalidation refetch — tốn 1 RTT).
 *
 * Cập nhật:
 *  - casesApi.getCase(caseId).current_round_id
 *  - casesApi.getCases() — entry tương ứng
 *  - roundsApi.getCaseRounds(caseId) — old current → completed, new current → active
 */
function patchAfterMoveRound({
  dispatch,
  caseId,
  newRoundId,
  meetingLink,
}: {
  dispatch: any
  caseId: string
  newRoundId: string
  meetingLink?: string | null
}) {
  // Patch Case.current_round_id (single + list)
  dispatch(
    casesApi.util.updateQueryData('getCase', caseId, (draft: any) => {
      draft.current_round_id = newRoundId
    })
  )
  dispatch(
    casesApi.util.updateQueryData('getCases', undefined, (draft: any[]) => {
      const c = draft.find((x) => x.id === caseId)
      if (c) c.current_round_id = newRoundId
    })
  )
  // Patch Round[].status — old → completed, new → active. Cũng set meeting_link nếu có.
  dispatch(
    roundsApi.util.updateQueryData('getCaseRounds', caseId, (draft: any[]) => {
      for (const r of draft) {
        if (r.status === 'active' && r.id !== newRoundId) {
          r.status = 'completed'
        }
        if (r.id === newRoundId) {
          r.status = 'active'
          if (meetingLink) r.meeting_link = meetingLink
        }
      }
    })
  )
}

export const {
  useGetCasesQuery,
  useGetCaseQuery,
  useCreateCaseMutation,
  useUpdateCaseMutation,
  useAssignCaseMutation,
  useUnassignTeamFromCaseMutation,
  useMoveToNextRoundMutation,
  useMoveToRoundMutation,
  useDeleteCaseMutation,
  useGetExamInfoQuery,
  useLazyGetExamInfoQuery,
  useGetEvidencePoolSummaryQuery,
} = casesApi
